/**
 * Wan VACE Hook
 *
 * Manages Wan VACE status, install/uninstall actions, and install progress
 * polling. Progress is reported via GET polling (not SSE) because the backend
 * stores jobs in memory.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  getInstallProgress,
  getWanVaceStatus,
  installWanVaceModel,
  selectWanVaceModel,
  uninstallWanVaceAll,
  uninstallWanVaceModel,
  type WanVaceInstallProgress,
  type WanVaceModelId,
  type WanVaceStatus,
} from '../services/wanVaceService';

const PROGRESS_POLL_MS = 1500;
// Canonical wire vocabulary (audit 2026-06-10 Stage 04): terminal states
// are 'completed' | 'failed'; the manager's step detail lives in message.
const TERMINAL_STATUSES = new Set(['completed', 'failed']);

export interface UseWanVaceReturn {
  status: WanVaceStatus | null;
  loading: boolean;
  error: string | null;
  installing: boolean;
  progress: WanVaceInstallProgress | null;
  refresh: () => Promise<void>;
  install: (modelId: WanVaceModelId | string) => Promise<void>;
  uninstallModel: (modelId: string) => Promise<void>;
  uninstallAll: () => Promise<void>;
  selectModel: (modelId: string) => Promise<void>;
  dismissError: () => void;
}

export function useWanVace(): UseWanVaceReturn {
  const [status, setStatus] = useState<WanVaceStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [installing, setInstalling] = useState(false);
  const [progress, setProgress] = useState<WanVaceInstallProgress | null>(null);

  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  const clearPollTimer = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearPollTimer();
    };
  }, [clearPollTimer]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await getWanVaceStatus();
      if (!mountedRef.current) return;
      setStatus(s);
    } catch (err) {
      if (!mountedRef.current) return;
      const message = err instanceof Error ? err.message : 'Failed to load Wan VACE status';
      setError(message);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const pollProgress = useCallback(
    (jobId: string) => {
      const tick = async () => {
        try {
          const p = await getInstallProgress(jobId);
          if (!mountedRef.current) return;
          setProgress(p);

          if (TERMINAL_STATUSES.has(p.status)) {
            clearPollTimer();
            setInstalling(false);
            if (p.status === 'failed') {
              setError(p.message || 'Installation failed');
            } else {
              await refresh();
            }
            return;
          }

          pollTimerRef.current = setTimeout(() => void tick(), PROGRESS_POLL_MS);
        } catch (err) {
          if (!mountedRef.current) return;
          clearPollTimer();
          setInstalling(false);
          const message = err instanceof Error ? err.message : 'Lost connection to install job';
          setError(message);
        }
      };
      void tick();
    },
    [clearPollTimer, refresh]
  );

  const install = useCallback(
    async (modelId: WanVaceModelId | string) => {
      setError(null);
      setInstalling(true);
      setProgress({
        jobId: '',
        status: 'starting',
        progress: 0,
        message: 'Starting installation...',
        model: modelId,
      });
      try {
        const response = await installWanVaceModel(modelId);
        if (!mountedRef.current) return;
        pollProgress(response.jobId);
      } catch (err) {
        if (!mountedRef.current) return;
        setInstalling(false);
        const message = err instanceof Error ? err.message : 'Failed to start installation';
        setError(message);
      }
    },
    [pollProgress]
  );

  const uninstallModel = useCallback(
    async (modelId: string) => {
      setError(null);
      try {
        await uninstallWanVaceModel(modelId);
        await refresh();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to uninstall model';
        setError(message);
      }
    },
    [refresh]
  );

  const uninstallAll = useCallback(async () => {
    setError(null);
    try {
      await uninstallWanVaceAll();
      await refresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to uninstall Wan VACE';
      setError(message);
    }
  }, [refresh]);

  const selectModel = useCallback(
    async (modelId: string) => {
      setError(null);
      try {
        await selectWanVaceModel(modelId);
        await refresh();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to select model';
        setError(message);
      }
    },
    [refresh]
  );

  const dismissError = useCallback(() => setError(null), []);

  return {
    status,
    loading,
    error,
    installing,
    progress,
    refresh,
    install,
    uninstallModel,
    uninstallAll,
    selectModel,
    dismissError,
  };
}
